import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

import type { TimeSeriesChartTheme } from './TimeSeriesChartTheme.types';

function getFontWeight(weight: Theme['typography']['fontWeightMedium']): number {
  return typeof weight === 'number' ? weight : 500;
}

function getBorderRadius(radius: Theme['shape']['borderRadius']): number {
  return typeof radius === 'number' ? radius : 4;
}

/** MUI typography sizes may be numbers (px) or CSS strings (`0.75rem`, `12px`); the canvas needs pixels. */
function toPixels(value: string | number | undefined, htmlFontSize: number, fallback: number): number {
  if (typeof value === 'number') {
    return value;
  }

  const parsed = Number.parseFloat(value ?? '');

  if (!Number.isFinite(parsed)) {
    return fallback;
  }

  return value?.trim().endsWith('rem') ? parsed * htmlFontSize : parsed;
}

/** A unitless line height is a multiple of the font size, as in CSS. */
function toLineHeightPixels(value: string | number | undefined, fontSize: number): number {
  if (typeof value === 'number') {
    return value * fontSize;
  }

  const parsed = Number.parseFloat(value ?? '');

  if (!Number.isFinite(parsed)) {
    return fontSize;
  }

  return value?.trim().endsWith('px') ? parsed : parsed * fontSize;
}

function createSectionLabelTheme(theme: Theme): TimeSeriesChartTheme['sectionLabel'] {
  const { overline, htmlFontSize } = theme.typography;
  const fontSize = toPixels(overline.fontSize, htmlFontSize, 12);

  return {
    color: theme.palette.text.secondary,
    fontFamily: overline.fontFamily,
    fontSize,
    fontWeight: typeof overline.fontWeight === 'number' ? overline.fontWeight : 400,
    lineHeight: toLineHeightPixels(overline.lineHeight, fontSize),
  };
}

export function createTimeSeriesChartTheme(theme: Theme): TimeSeriesChartTheme {
  const borderRadius = getBorderRadius(theme.shape.borderRadius);

  return {
    guideLine: {
      color: theme.palette.grey[500],
      labelBackground: alpha(theme.palette.grey[700], 0.9),
      labelColor: theme.palette.common.white,
      labelFontFamily: theme.typography.caption.fontFamily,
      labelFontWeight: getFontWeight(theme.typography.fontWeightMedium),
      // MUI caption typography does not match the compact 10px/14px chart label, so preserve it explicitly.
      labelFontSize: 10,
      labelLineHeight: 14,
      labelBorderRadius: borderRadius,
    },
    sectionLabel: createSectionLabelTheme(theme),
    symbol: {
      borderColor: theme.palette.background.paper,
    },
    trackLabel: {
      color: theme.palette.text.secondary,
      backdropColor: theme.palette.background.paper,
      markerBorderRadius: 0,
    },
    axis: {
      labelColor: theme.palette.text.secondary,
      splitLineColor: theme.palette.divider,
    },
    rangeTrack: {
      fallbackColor: theme.palette.primary.main,
      rowBorderRadius: borderRadius,
      barBorderRadius: borderRadius,
    },
    slider: {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.background.paper,
      // The selected window is the readable part of the slider, so it carries the brand colour.
      fillerColor: alpha(theme.palette.primary.main, 0.16),
      handleColor: theme.palette.background.paper,
      handleBorderColor: theme.palette.primary.main,
      dataShadowColor: alpha(theme.palette.primary.main, 0.32),
      borderRadius,
    },
  };
}
